import { FC, useState, useCallback } from "react";
import {
  Box, Typography, Button, Alert, FormControl, Select, MenuItem, Stack,
  LinearProgress, CircularProgress
} from "@mui/material";
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { ItemsModal, CustomAction } from '@/components/common/ItemsModal';
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/app/store";
import { FolderFileEntry } from "@/types/document.types";
import { selectFolderUploading, selectFolderUploadError, selectFolderUploadProgress, selectFolderUploadResult } from "@/features/documents/documentsSelectors";
import { uploadFolder, clearFolderUploadState } from "@/features/documents/documentsThunks";
import { extractFilesFromInput, extractFilesFromDrop, getRootFolderName } from "../docHelper";
import { validateFolder, FolderValidationResult } from "../validations";

interface FolderUploadModalProps {
  open: boolean;
  onClose: () => void;
}

const FolderUploadModal: FC<FolderUploadModalProps> = ({ open, onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const isFolderUploading = useSelector(selectFolderUploading);
  const folderUploadError = useSelector(selectFolderUploadError);
  const folderUploadProgress = useSelector(selectFolderUploadProgress);
  const folderUploadResult = useSelector(selectFolderUploadResult);

  const [selectedTag, setSelectedTag] = useState('');
  const [folderEntries, setFolderEntries] = useState<FolderFileEntry[]>([]);
  const [validationResult, setValidationResult] = useState<FolderValidationResult | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [folderName, setFolderName] = useState('');

  const hasFinished = folderUploadResult !== null;

  const processFolderFiles = useCallback((entries: FolderFileEntry[]) => {
    if (entries.length === 0) return;
    const rootName = getRootFolderName(entries);
    setFolderName(rootName);
    const validation = validateFolder(entries);
    setValidationResult(validation);
    setFolderEntries(validation.validFiles);
  }, []);

  const handleFolderSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const entries = extractFilesFromInput(files);
      processFolderFiles(entries);
    }
  }, [processFolderFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (e.dataTransfer.items) {
      const entries = await extractFilesFromDrop(e.dataTransfer.items);
      processFolderFiles(entries);
    }
  }, [processFolderFiles]);

  const handleConfirmUpload = useCallback(async () => {
    if (!folderEntries.length || !selectedTag) return;
    await dispatch(uploadFolder({ files: folderEntries, tag: selectedTag }));
  }, [dispatch, folderEntries, selectedTag]);

  const handleClose = useCallback(() => {
    dispatch(clearFolderUploadState());
    setSelectedTag('');
    setFolderEntries([]);
    setValidationResult(null);
    setFolderName('');
    setIsDragOver(false);
    onClose();
  }, [dispatch, onClose]);

  const customActions: CustomAction[] = hasFinished
    ? [{ label: 'Cerrar', onClick: handleClose, variant: 'contained' }]
    : [
        { label: 'Cancelar', onClick: handleClose, variant: 'outlined', disabled: isFolderUploading },
        {
          label: 'Subir Carpeta',
          onClick: handleConfirmUpload,
          variant: 'contained',
          disabled: !folderEntries.length || !selectedTag || isFolderUploading,
          loading: isFolderUploading,
        },
      ];

  return (
    <ItemsModal
      open={open}
      onClose={handleClose}
      title="Subir Carpeta"
      showCustomActions={true}
      customActions={customActions}
      isLoading={isFolderUploading}
    >
      <Stack spacing={3}>
        {folderUploadError && <Alert severity="error">{folderUploadError}</Alert>}

        {/* Result summary */}
        {hasFinished && (
          <Stack spacing={2}>
            {folderUploadResult.successfulUploads.length > 0 && (
              <Alert severity="success">
                {folderUploadResult.successfulUploads.length} archivo(s) subido(s) correctamente.
              </Alert>
            )}
            {folderUploadResult.failedUploads.length > 0 && (
              <Alert severity="warning">
                {folderUploadResult.failedUploads.length} archivo(s) no se pudieron subir:
                <Box component="ul" sx={{ m: 0, pl: 2, mt: 1 }}>
                  {folderUploadResult.failedUploads.map((f, i) => (
                    <li key={i}>
                      <Typography variant="caption">{f.relativePath}: {f.error}</Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}
          </Stack>
        )}

        {/* Upload progress */}
        {isFolderUploading && folderUploadProgress && (
          <Stack spacing={2}>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {folderUploadProgress.completedFiles + folderUploadProgress.failedFiles} de {folderUploadProgress.totalFiles} archivos procesados
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {folderUploadProgress.overallProgress}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={folderUploadProgress.overallProgress}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 1, p: 1 }}>
              {folderUploadProgress.fileStatuses.map((fs, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  {fs.status === 'success' && <CheckCircleOutlineIcon color="success" sx={{ fontSize: 18 }} />}
                  {fs.status === 'error' && <ErrorOutlineIcon color="error" sx={{ fontSize: 18 }} />}
                  {fs.status === 'uploading' && <CircularProgress size={16} />}
                  {fs.status === 'pending' && <ScheduleIcon color="disabled" sx={{ fontSize: 18 }} />}
                  <Typography variant="body2" noWrap sx={{ flex: 1 }}>{fs.relativePath}</Typography>
                  {fs.status === 'uploading' && (
                    <Typography variant="caption" color="text.secondary">{Math.round(fs.progress)}%</Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Stack>
        )}

        {/* Selection UI (only before uploading starts) */}
        {!isFolderUploading && !hasFinished && (
          <>
            {/* Tag selector */}
            <FormControl fullWidth>
              <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                Etiqueta
              </Typography>
              <Select
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
              >
                <MenuItem value="">Selecciona una etiqueta</MenuItem>
                <MenuItem value="Académico">Académico</MenuItem>
                <MenuItem value="Administrativo">Administrativo</MenuItem>
                <MenuItem value="General">General</MenuItem>
              </Select>
            </FormControl>

            {/* Drop zone */}
            <Box>
              <Typography variant="body2" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
                Carpeta
              </Typography>
              <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                sx={{
                  border: `2px dashed ${isDragOver ? '#1976d2' : '#e5e7eb'}`,
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  backgroundColor: isDragOver ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                }}
              >
                <CloudUploadOutlinedIcon sx={{ fontSize: 40, color: isDragOver ? 'primary.main' : '#9ca3af', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Arrastra y suelta una carpeta aqui
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  o
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CreateNewFolderOutlinedIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Seleccionar Carpeta
                  <input
                    type="file"
                    hidden
                    onChange={handleFolderSelect}
                    {...({ webkitdirectory: "", directory: "" } as any)}
                    multiple
                  />
                </Button>
              </Box>
            </Box>

            {/* Validation results */}
            {validationResult && (
              <Stack spacing={1}>
                {folderName && (
                  <Typography variant="body2" fontWeight={600}>
                    Carpeta: {folderName}
                  </Typography>
                )}
                <Alert severity="info">
                  {validationResult.validFiles.length} archivo(s) valido(s) de {validationResult.totalFiles} encontrado(s).
                </Alert>
                {validationResult.invalidFiles.length > 0 && (
                  <Alert severity="warning">
                    {validationResult.invalidFiles.length} archivo(s) seran omitidos:
                    <Box component="ul" sx={{ m: 0, pl: 2, mt: 1 }}>
                      {validationResult.invalidFiles.map((f, i) => (
                        <li key={i}>
                          <Typography variant="caption">{f.fileName}: {f.error}</Typography>
                        </li>
                      ))}
                    </Box>
                  </Alert>
                )}
                {validationResult.validFiles.length > 0 && (
                  <Box sx={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 1, p: 1 }}>
                    {validationResult.validFiles.map((entry, idx) => (
                      <Typography key={idx} variant="body2" noWrap sx={{ py: 0.25 }}>
                        {entry.relativePath}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Stack>
            )}
          </>
        )}
      </Stack>
    </ItemsModal>
  );
};

export default FolderUploadModal;
